import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LoaderCircle } from 'lucide-react';
import { LiveKitRoom, type LiveKitRoomProps, useTranscriptions } from '@livekit/components-react';
import { Button, Modal } from 'common/components';
import { TranscriptionWithSpeaker, getSpeakerType, Transcripts } from 'components/SyncTranscript';
import { useGetLivekitRoomDetails } from 'services/hooks/queries';
import { usePostPersistTranscript } from 'services/hooks/queries/usePostPersistTranscript';
import { useGetCallTranscript } from 'services/hooks/mutations/useGetCallTranscript';
import { toast } from 'react-toastify';

import styles from './LiveMonitorTranscript.module.scss';

const LiveKitRoomWrapper = LiveKitRoom as unknown as React.FC<LiveKitRoomProps>;

type LivekitTranscriptProps = {
    accumulatedTranscriptions: TranscriptionWithSpeaker[];
    onNewTranscriptions?: (transcriptions: TranscriptionWithSpeaker[]) => void;
};

const LivekitTranscriptStream: React.FC<LivekitTranscriptProps> = ({
    accumulatedTranscriptions,
    onNewTranscriptions,
}) => {
    const transcriptions = useTranscriptions();
    const previousTranscriptionsRef = useRef<Array<{ text: string; participantIdentity: string }>>([]);
    const pendingTranscriptionsRef = useRef<TranscriptionWithSpeaker[] | null>(null);
    const rafIdRef = useRef<number | null>(null);

    const flushPendingUpdate = useCallback(() => {
        if (pendingTranscriptionsRef.current !== null && onNewTranscriptions) {
            onNewTranscriptions(pendingTranscriptionsRef.current);
            pendingTranscriptionsRef.current = null;
        }
        rafIdRef.current = null;
    }, [onNewTranscriptions]);

    useEffect(() => {
        if (!transcriptions?.length) return;

        const previousTranscriptions = previousTranscriptionsRef.current;
        let hasChanges = false;

        const updatedTranscriptions: TranscriptionWithSpeaker[] = transcriptions.map((transcription, index) => {
            const participantIdentity = transcription.participantInfo?.identity || 'unknown';
            const currentText = transcription.text;
            const previousTranscription = previousTranscriptions[index];

            const hasChanged =
                !previousTranscription ||
                previousTranscription.text !== currentText ||
                previousTranscription.participantIdentity !== participantIdentity;

            hasChanges = hasChanges || hasChanged;

            const existingTranscription = accumulatedTranscriptions[index];
            if (existingTranscription && !hasChanged) {
                return existingTranscription;
            }

            return {
                text: currentText,
                participantIdentity,
                speakerType: getSpeakerType(participantIdentity),
                timestamp: Date.now(),
                id: `${participantIdentity}-${index}`,
            };
        });

        if (!hasChanges) return;

        previousTranscriptionsRef.current = transcriptions.map((transcription) => ({
            text: transcription.text,
            participantIdentity: transcription.participantInfo?.identity || 'unknown',
        }));

        pendingTranscriptionsRef.current = updatedTranscriptions;
        if (rafIdRef.current == null) {
            rafIdRef.current = requestAnimationFrame(flushPendingUpdate);
        }
    }, [transcriptions, accumulatedTranscriptions, flushPendingUpdate]);

    useEffect(
        () => () => {
            if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current);
        },
        []
    );

    return <Transcripts transcriptions={accumulatedTranscriptions} containerClassName={styles.transcriptList} />;
};

export interface LiveMonitorTranscriptProps {
    callId: string;
    isCallInProgress: boolean;
}

export const LiveMonitorTranscript: React.FC<LiveMonitorTranscriptProps> = ({ callId, isCallInProgress }) => {
    const [accumulatedTranscriptions, setAccumulatedTranscriptions] = useState<TranscriptionWithSpeaker[]>([]);
    const [persistedTranscriptions, setPersistedTranscriptions] = useState<TranscriptionWithSpeaker[]>([]);
    const [isFullTranscriptOpen, setIsFullTranscriptOpen] = useState(false);
    const transcriptContainerRef = useRef<HTMLDivElement | null>(null);

    const enabled = Boolean(callId) && isCallInProgress;
    const { livekitRoomDetails, isLoadingLivekitRoomDetails } = useGetLivekitRoomDetails(callId, enabled);
    const {
        isLoading: isLoadingPersistTranscript,
        isPersistTranscriptSuccess,
        isFetched: isFetchedPersistTranscript,
    } = usePostPersistTranscript(livekitRoomDetails?.Room, enabled);
    const { getCallTranscriptWithRetry, isCallTranscriptWithRetryLoading } = useGetCallTranscript();

    useEffect(() => {
        setAccumulatedTranscriptions([]);
        setPersistedTranscriptions([]);
    }, [callId]);

    useEffect(() => {
        if (!isPersistTranscriptSuccess || !enabled) return;

        const timer = setTimeout(() => {
            getCallTranscriptWithRetry(callId, {
                onSuccess: (transcriptData) => {
                    const formattedTranscriptions = transcriptData.speeches.map((speech) => ({
                        text: speech.text,
                        participantIdentity: speech.speaker,
                        speakerType: getSpeakerType(speech.speaker),
                        timestamp: speech.timestamp_ns,
                        id: speech.order.toString(),
                    }));
                    setPersistedTranscriptions(formattedTranscriptions);
                },
                onError: () => {
                    toast.error('Error fetching history transcriptions');
                },
            });
        }, 1000);

        return () => clearTimeout(timer);
    }, [isPersistTranscriptSuccess, enabled, callId, getCallTranscriptWithRetry]);

    useEffect(() => {
        if (accumulatedTranscriptions.length === 0 || !transcriptContainerRef.current) return;
        transcriptContainerRef.current.scrollTo({
            top: transcriptContainerRef.current.scrollHeight,
            behavior: 'smooth',
        });
    }, [accumulatedTranscriptions]);

    const handleUpdateTranscriptions = useCallback((updatedTranscriptions: TranscriptionWithSpeaker[]) => {
        setAccumulatedTranscriptions(updatedTranscriptions);
    }, []);

    if (!isCallInProgress) {
        return (
            <div className={styles.emptyState} data-testid='live-monitor-transcript-idle'>
                Live transcript is available when the call is in progress.
            </div>
        );
    }

    const isLoadingHistory = isCallTranscriptWithRetryLoading || isLoadingPersistTranscript;

    return (
        <div className={styles.panel} data-testid='live-monitor-transcript'>
            <div className={styles.scrollArea} ref={transcriptContainerRef}>
                {isLoadingHistory ? (
                    <div className={styles.loading}>
                        <LoaderCircle size={18} className={styles.spinner} />
                        Loading transcript history…
                    </div>
                ) : (
                    <Transcripts transcriptions={persistedTranscriptions} containerClassName={styles.transcriptList} />
                )}

                {livekitRoomDetails?.URL && livekitRoomDetails?.Token ? (
                    <LiveKitRoomWrapper
                        serverUrl={livekitRoomDetails.URL}
                        token={livekitRoomDetails.Token}
                        connect={enabled && isFetchedPersistTranscript}
                    >
                        <LivekitTranscriptStream
                            accumulatedTranscriptions={accumulatedTranscriptions}
                            onNewTranscriptions={handleUpdateTranscriptions}
                        />
                    </LiveKitRoomWrapper>
                ) : isLoadingLivekitRoomDetails ? (
                    <div className={styles.loading}>
                        <LoaderCircle size={18} className={styles.spinner} />
                        Connecting to live room…
                    </div>
                ) : (
                    <div className={styles.emptyState}>Unable to load live room details.</div>
                )}
            </div>

            <div className={styles.footer}>
                <Button variant='outline-secondary' size='sm' onClick={() => setIsFullTranscriptOpen(true)}>
                    Show full transcript
                </Button>
            </div>

            <Modal
                isOpen={isFullTranscriptOpen}
                onClose={() => setIsFullTranscriptOpen(false)}
                title='Full transcript'
                size='lg'
            >
                <div className={styles.fullTranscriptBody}>
                    <Transcripts
                        transcriptions={[...persistedTranscriptions, ...accumulatedTranscriptions]}
                        containerClassName={styles.transcriptList}
                    />
                </div>
            </Modal>
        </div>
    );
};
